import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type GlobalMusicTrack = {
  id: string;
  title: string;
  artist: string;
  src: string;
  downloadUrl?: string;
};

const DEFAULT_TRACK: GlobalMusicTrack = {
  id: 'they-see-us-now',
  title: 'They see us now',
  artist: 'AG',
  src: 'https://skoleom.s3.eu-west-3.amazonaws.com/skoleom-toons/music/music.wav',
  downloadUrl: 'https://skoleom.s3.eu-west-3.amazonaws.com/skoleom-toons/music/music.wav',
};

type GlobalMusicContextValue = {
  audioElement: HTMLAudioElement | null;
  currentTrack: GlobalMusicTrack | null;
  hasTrack: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  playTrack: (track?: GlobalMusicTrack) => void;
  toggleTrack: (track?: GlobalMusicTrack) => void;
  restartTrack: (track?: GlobalMusicTrack) => void;
  pauseTrack: () => void;
  seekPercent: (value: number) => void;
  setTrackVolume: (value: number) => void;
};

const GlobalMusicContext = createContext<GlobalMusicContextValue | null>(null);

function waitForCanPlay(audio: HTMLAudioElement, timeoutMs = 5000): Promise<void> {
  return new Promise<void>((resolve) => {
    if (audio.readyState >= 3) {
      resolve();
      return;
    }

    let timer: ReturnType<typeof setTimeout> = setTimeout(() => {
      audio.removeEventListener('canplay', done);
      resolve();
    }, timeoutMs);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };

    audio.addEventListener('canplay', done, { once: true });
    // Safety timeout so we never hang forever
    timer = setTimeout(() => {
      audio.removeEventListener('canplay', done);
      resolve();
    }, timeoutMs);
  });
}

/**
 * Point the audio element at a new src and wait until it can play.
 * No-ops (but still waits) if the src is already loaded.
 */
async function loadSrc(
  audio: HTMLAudioElement,
  srcRef: React.MutableRefObject<string>,
  src: string,
): Promise<void> {
  if (srcRef.current === src) {
    // Already the correct src — just wait if still buffering
    await waitForCanPlay(audio);
    return;
  }

  // Attach canplay BEFORE mutating src so the event is never missed
  const ready = waitForCanPlay(audio);
  srcRef.current = src;
  audio.src = src;
  audio.load();
  await ready;
}

// ─── provider ───────────────────────────────────────────────────────────────

export function GlobalMusicProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Track the src we assigned ourselves — never compare against audio.src
  // which the browser resolves to an absolute URL (always mismatches)
  const currentSrcRef = useRef<string>('');

  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  // Pre-set DEFAULT_TRACK so hasTrack === true from mount
  // (needed for PersistentMusicBar & Music component to show on first render)
  const [currentTrack, setCurrentTrack] = useState<GlobalMusicTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Expose the raw audio element (needed by audiomotion-analyzer)
  useEffect(() => {
    setAudioElement(audioRef.current);
  }, []);

  // Attach all persistent audio event listeners once
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      setCurrentTime(audio.currentTime);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    // Reset duration/time when a new src starts loading
    const onEmptied = () => {
      setDuration(0);
      setCurrentTime(0);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('emptied', onEmptied);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('emptied', onEmptied);
    };
  }, []);

  const normalizeTrack = useCallback(
    (track?: GlobalMusicTrack): GlobalMusicTrack => track ?? currentTrack ?? DEFAULT_TRACK,
    [currentTrack],
  );

  // ── public API ───────────────────────────────────────────────────────────

  /** Load (if needed) then play. Always starts from current position. */
  const playTrack = useCallback(
    async (track?: GlobalMusicTrack) => {
      const nextTrack = normalizeTrack(track);
      const audio = audioRef.current;
      if (!audio) return;

      await loadSrc(audio, currentSrcRef, nextTrack.src);
      setCurrentTrack(nextTrack);

      try {
        await audio.play();
      } catch (err) {
        console.warn('[GlobalMusic] playTrack blocked:', err);
      }
    },
    [normalizeTrack],
  );

  /** Toggle play/pause for the current track, or switch+play a new one. */
  const toggleTrack = useCallback(
    async (track?: GlobalMusicTrack) => {
      const nextTrack = normalizeTrack(track);
      const audio = audioRef.current;
      if (!audio) return;

      const isSameSrc = currentSrcRef.current === nextTrack.src;

      if (isSameSrc) {
        if (audio.paused) {
          try {
            await audio.play();
          } catch (err) {
            console.warn('[GlobalMusic] toggleTrack play blocked:', err);
          }
        } else {
          audio.pause();
        }
        return;
      }

      // Different track → load then play
      await loadSrc(audio, currentSrcRef, nextTrack.src);
      setCurrentTrack(nextTrack);

      try {
        await audio.play();
      } catch (err) {
        console.warn('[GlobalMusic] toggleTrack (new track) play blocked:', err);
      }
    },
    [normalizeTrack],
  );

  /** Seek to 0 and play from the beginning. */
  const restartTrack = useCallback(
    async (track?: GlobalMusicTrack) => {
      const nextTrack = normalizeTrack(track);
      const audio = audioRef.current;
      if (!audio) return;

      await loadSrc(audio, currentSrcRef, nextTrack.src);
      setCurrentTrack(nextTrack);

      audio.currentTime = 0;
      setCurrentTime(0);

      try {
        await audio.play();
      } catch (err) {
        console.warn('[GlobalMusic] restartTrack play blocked:', err);
      }
    },
    [normalizeTrack],
  );

  const pauseTrack = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const seekPercent = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const nextTime = Math.max(0, Math.min(1, value / 100)) * audio.duration;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  }, []);

  const setTrackVolume = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = Math.max(0, Math.min(1, value));
  }, []);

  // ── derived ──────────────────────────────────────────────────────────────

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  const value = useMemo<GlobalMusicContextValue>(
    () => ({
      audioElement,
      currentTrack,
      hasTrack: Boolean(currentTrack),
      isPlaying,
      currentTime,
      duration,
      progress,
      playTrack,
      toggleTrack,
      restartTrack,
      pauseTrack,
      seekPercent,
      setTrackVolume,
    }),
    [
      audioElement,
      currentTrack,
      isPlaying,
      currentTime,
      duration,
      progress,
      playTrack,
      toggleTrack,
      restartTrack,
      pauseTrack,
      seekPercent,
      setTrackVolume,
    ],
  );

  return (
    <GlobalMusicContext.Provider value={value}>
      {children}
      {/* crossOrigin="anonymous" is required for audiomotion-analyzer Web Audio API access */}
      <audio ref={audioRef} preload="metadata" crossOrigin="anonymous" className="hidden" />
    </GlobalMusicContext.Provider>
  );
}

export function useGlobalMusic() {
  const ctx = useContext(GlobalMusicContext);
  if (!ctx) {
    throw new Error('useGlobalMusic must be used within GlobalMusicProvider');
  }
  return ctx;
}
